import { toDatabaseName } from '@/lib/naming'
import { normalizePhone } from '@/lib/phone'
import type {
  ClientDetail,
  ClientSavePayload,
  ClientStatusId,
  ClientTypeId,
  PbiGroupOption,
} from './types'

export type ClientFormValues = {
  name: string
  phone: string
  email: string
  principalContact: string
  address: string
  street: string
  suburb: string
  state: string
  postcode: string
  powerBIWorkspace: string
  powerBIWorkspaceName?: string
  statusId: ClientStatusId
  typeId: ClientTypeId
  showProjectStatus?: boolean
  showAdvisoryMapping?: boolean
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

export const acceptedFileTypes = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/tiff',
  'image/webp',
  'image/bmp',
  'image/gif',
]

export const MAX_LOGO_FILE_SIZE_MB = 2
export const MAX_LOGO_FILE_SIZE_BYTES = MAX_LOGO_FILE_SIZE_MB * 1024 * 1024

export const DEFAULT_CLIENT_TEXT_COLOR = '#000000'

export function colorFieldValueToHex(v: unknown): string | undefined {
  if (v == null || v === '') return undefined
  if (typeof v === 'string') {
    const t = v.trim()
    return t || undefined
  }
  if (
    typeof v === 'object' &&
    v !== null &&
    'toHexString' in v &&
    typeof (v as { toHexString: () => string }).toHexString === 'function'
  ) {
    try {
      const hex = (v as { toHexString: () => string }).toHexString()
      return hex?.trim() || undefined
    } catch {
      return undefined
    }
  }
  return undefined
}

export const baseClientTypeOptions: Array<{ label: string; value: ClientTypeId }> = [
  { label: 'Standard', value: 1 },
  { label: 'Restricted', value: 2 },
]

export function statusToLabel(statusId: ClientStatusId) {
  return statusId === 1 ? 'Active' : 'Inactive'
}

export function composeAddress(values: Pick<ClientFormValues, 'address' | 'street' | 'suburb' | 'state' | 'postcode'>) {
  if (values.address?.trim()) return values.address.trim()
  return [values.street, values.suburb, values.state, values.postcode]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ')
}

export function normalizeWorkspaceValue(value: unknown) {
  if (value === null || value === undefined || value === '') return undefined
  return String(value)
}

export function hasPlatformType(platformTypes: Array<number | string> | undefined, target: number) {
  return (platformTypes || []).some((platformType) => Number(platformType) === target)
}

export function buildClientPayloadFromForm(
  values: ClientFormValues,
  pbiGroups: PbiGroupOption[],
  logoBase64: string,
  statusOverride?: ClientStatusId
): ClientSavePayload {
  const workspaceValue = normalizeWorkspaceValue(values.powerBIWorkspace)
  const selectedPbiGroup = pbiGroups.find((group) => String(group.value) === workspaceValue)

  return {
    statusId: statusOverride ?? values.statusId,
    typeId: values.typeId,
    name: values.name,
    phone: normalizePhone(values.phone),
    email: values.email,
    contact: values.principalContact,
    address: composeAddress(values),
    street: values.street,
    suburb: values.suburb,
    state: values.state,
    postcode: values.postcode,
    powerBIWorkspace: workspaceValue,
    powerBIWorkspaceName: selectedPbiGroup?.name || values.powerBIWorkspaceName || '',
    databaseName: toDatabaseName(values.name),
    showProjectStatus: !!values.showProjectStatus,
    showAdvisoryMapping: !!values.showAdvisoryMapping,
    logo: logoBase64,
    isImage: !!logoBase64,
    primaryColor: colorFieldValueToHex(values.primaryColor),
    secondaryColor: colorFieldValueToHex(values.secondaryColor),
    textColor: colorFieldValueToHex(values.textColor) || DEFAULT_CLIENT_TEXT_COLOR,
  }
}

export function buildClientPayloadFromDetail(
  detail: ClientDetail,
  pbiGroups: PbiGroupOption[],
  statusOverride?: ClientStatusId
): ClientSavePayload {
  const workspaceValue = normalizeWorkspaceValue(detail.powerBIWorkspace)
  const selectedPbiGroup = pbiGroups.find((group) => String(group.value) === workspaceValue)

  return {
    statusId: statusOverride ?? detail.statusId,
    typeId: detail.typeId,
    name: detail.name,
    phone: normalizePhone(detail.phone),
    email: detail.email,
    contact: detail.principalContact,
    address: detail.address,
    street: detail.street || '',
    suburb: detail.suburb || '',
    state: detail.state || '',
    postcode: detail.postcode || '',
    powerBIWorkspace: workspaceValue,
    powerBIWorkspaceName: selectedPbiGroup?.name || detail.powerBIWorkspaceName || '',
    databaseName: toDatabaseName(detail.name),
    showProjectStatus: !!detail.showProjectStatus,
    showAdvisoryMapping: !!detail.showAdvisoryMapping,
    primaryColor: detail.primaryColor?.trim() || undefined,
    secondaryColor: detail.secondaryColor?.trim() || undefined,
    textColor: detail.textColor?.trim() || DEFAULT_CLIENT_TEXT_COLOR,
  }
}
